import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function DocketPrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [docket, setDocket] = useState(null);
  const [company, setCompany] = useState(null);
  const [product, setProduct] = useState(null);
  const [po, setPO] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/dockets/${id}`);
        setDocket(d);
        // Tolerant per-lookup fetches: missing references shouldn't fail the whole page.
        if (d.Company_ID) {
          api.get(`/companies/${d.Company_ID}`).then(({ data }) => setCompany(data)).catch(() => {});
        }
        if (d.Product_ID) {
          api.get(`/inventory/${d.Product_ID}`).then(({ data }) => setProduct(data)).catch(() => {});
        }
        if (d.Source_PO_ID) {
          api.get(`/purchase-orders/${d.Source_PO_ID}`).then(({ data }) => setPO(data)).catch(() => {});
        }
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, [id]);

  if (err) return <div className="p-8 text-red-700">{err}</div>;
  if (!docket) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="bg-white min-h-screen text-zinc-900" data-testid="docket-print-page">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden bg-zinc-100 border-b border-zinc-200 px-6 py-3 flex items-center justify-between">
        <button
          onClick={() => {
            // The print page is usually opened in a new tab (window.opener set),
            // so `navigate(-1)` does nothing. Try closing the tab first, fall back
            // to navigating to /dockets if this is a same-tab visit.
            if (window.opener) {
              window.close();
              return;
            }
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/dockets");
            }
          }}
          data-testid="docket-print-back"
          className="es-btn-secondary"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button data-testid="docket-print-trigger" onClick={() => window.print()} className="es-btn-primary">
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      {/* Printable docket sheet */}
      <div className="max-w-[800px] mx-auto p-10 print:p-6">
        <header className="flex items-start justify-between border-b-2 border-zinc-900 pb-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">EDENSTONE MASONRY</h1>
            <p className="text-sm text-zinc-600 mt-1">Dispatch Docket</p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">Docket #</div>
            <div className="font-mono text-xl font-bold">{docket.Docket_Number}</div>
            <div className="text-xs text-zinc-500 mt-1">{new Date().toLocaleDateString()}</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-8 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Customer</div>
            <div className="font-semibold text-lg">{company?.Company_Name || "—"}</div>
            {company?.Phone && <div className="text-sm text-zinc-700">{company.Phone}</div>}
            {company?.Email && <div className="text-sm text-zinc-700">{company.Email}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Pickup Deadline</div>
            <div className="font-mono text-lg">{docket.Pickup_Deadline || "—"}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3 mb-1">Status</div>
            <div className="text-sm font-semibold">{docket.Docket_Status}</div>
          </div>
        </section>

        <section className="mb-6">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Delivery Address</div>
          <div className="border border-zinc-300 rounded-md p-3 min-h-[3rem] whitespace-pre-wrap">{docket.Delivery_Address || "—"}</div>
        </section>

        <section className="mb-6">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Order Line</div>
          <table className="w-full border border-zinc-300 text-sm">
            <thead className="bg-zinc-100">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Product</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">SKU</th>
                <th className="text-right px-3 py-2 border-b border-zinc-300">Qty to pick up</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3 py-3 align-top">
                  <div className="font-semibold">{product?.Product_Name || "—"}</div>
                  {product?.Colour && <div className="text-xs text-zinc-500">Colour: {product.Colour}</div>}
                </td>
                <td className="px-3 py-3 font-mono">{product?.SKU || "—"}</td>
                <td className="text-right px-3 py-3 font-mono text-lg font-bold">{docket.Quantity_To_Pick_Up}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {docket.Internal_Notes && (
          <section className="mb-6">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Internal Notes</div>
            <div className="border border-zinc-300 rounded-md p-3 whitespace-pre-wrap text-sm">{docket.Internal_Notes}</div>
          </section>
        )}

        {po && (
          <section className="mb-6 text-xs text-zinc-600 print:text-[10px]">
            <div className="border-l-2 border-brand-500 pl-3">
              Source PO: <strong>{po.id?.slice(0, 8)}</strong>
              {po.Parsed_Data?.po_reference && <> · Ref <span className="font-mono">{po.Parsed_Data.po_reference}</span></>}
              {po.Parsed_Data?.customer_email && <> · {po.Parsed_Data.customer_email}</>}
            </div>
          </section>
        )}

        <footer className="grid grid-cols-2 gap-12 mt-12 pt-6 border-t border-zinc-300">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Picked by</div>
            <div className="border-b border-zinc-400 h-10"></div>
            <div className="text-[10px] text-zinc-500 mt-1">Signature / date</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Received by</div>
            <div className="border-b border-zinc-400 h-10"></div>
            <div className="text-[10px] text-zinc-500 mt-1">Signature / date</div>
          </div>
        </footer>
      </div>
    </div>
  );
}
